import { Form } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useValue from "../../../../hooks/useValue";
import { General } from "../../../../util/General";
import { CustomNotification } from "../../../../util/CustomNotification";
import * as webUserManagementService from "../../../../services/webUserManagementService";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";

import type { AddUpdateUserFormInterface, CustomerLocationData, GetUserByIdResponse } from "../../../../shared/interface/UserManagement/user.interface";
import type FixMeLater from "../../../../shared/types/fixMeLater.type";

export const useWebUserForm = () => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id, view } = useParams<{ id: string; view?: string }>();

  /* STATE */
  const [readEnabled] = useState(Boolean(view));
  const [roleName, setRoleName] = useState<string | null>(null);
  const [customerLocationData, setCustomerLocationData] = useState<CustomerLocationData[]>([]);
  const [addCustomer, setAddCustomer] = useState<boolean>(id ? false : true);
  const [formattedLocationList, setFormattedLocationList] = useState<any[]>([]);

  const [roleList, setRoleList] = useState<any[]>([]);
  const [roleListLoading, setRoleListLoading] = useState(false);

  /* CUSTOM HOOKS */
  const { loading: customerListLoading, masterList: customerMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);
  const { loading: locationListLoading, masterList: locationMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]);

  /* form loading */
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(false);

  /* submit button loading  */
  const { value: saving, handleDisableValue: handleDisableSaving, handleEnableValue: handleEnableSaving } = useValue(false);

  /* HANDLERS */

  const handleCustomerChange = (value: number | null) => {
    const selectedCustomer = customerMasterList?.find((customer: FixMeLater) => customer.id === value);
    setCustomerLocationData((prev) => [
      ...prev,
      {
        customer_id: value,
        customer_name: selectedCustomer?.customer_name,
        customer_location_list: []
      }
    ]);
    setAddCustomer(false);
  };

  const handleChangeLocation = (value: number[] | FixMeLater, customerId: number | null) => {
    /* Find the index of the customer in the array */
    const customerIndex = customerLocationData?.findIndex((x) => x.customer_id === customerId);

    /*  If the customer is found */
    if (customerIndex !== -1 && customerIndex !== undefined) {
      /*  Create a copy of the current state */
      const updatedLocations = [...customerLocationData];

      /*  Update the specific customer's location list */
      updatedLocations[customerIndex] = {
        ...updatedLocations[customerIndex],
        customer_location_list: value
      };

      /* Update the state with the modified array */
      setCustomerLocationData(updatedLocations);
    }
  };

  const handleRemoveCustomerLocationData = (customerId: number | null) => {
    const updatedCustomerLocationData = customerLocationData?.filter((customer) => customer?.customer_id !== customerId);
    setCustomerLocationData(updatedCustomerLocationData);
  };

  const openCustomerSelection = () => {
    setAddCustomer(true);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const checkCustomerLocationValidation = (customerLocationData: CustomerLocationData[]) => {
    /* Check if customerLocationData is empty or not provided */
    if (!customerLocationData || customerLocationData.length === 0) {
      CustomNotification.showErrorMessage("Customer selection is required.");
      return false;
    }

    for (const item of customerLocationData) {
      /* Check if the customer is selected (customer_id is not null or undefined) */
      if (!item?.customer_id) {
        CustomNotification.showErrorMessage("Customer selection is required.");
        return false;
      }

      /* Check if at least one location is selected (customer_location_list is not empty) */
      if (!item?.customer_location_list || item?.customer_location_list.length === 0) {
        CustomNotification.showErrorMessage(`Customer '${item?.customer_name}' must have at least one location selected.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (formData: AddUpdateUserFormInterface) => {
    try {
      handleEnableSaving();

      /* Check if the customer is selected and Check if at least one location is selected */
      if (!checkCustomerLocationValidation(customerLocationData)) return;

      const formattedCustomerLocationData = customerLocationData?.map(({ customer_name, ...rest }) => rest) || [];

      const formattedData = General.trimFormValues({ ...formData, customerLocationList: formattedCustomerLocationData });

      if (id) {
        const updateResponse = await webUserManagementService.updateUser(formattedData, +id);
        CustomNotification.showSuccessMessage(updateResponse.message);
      } else {
        const addResponse = await webUserManagementService.createUser(formattedData);
        CustomNotification.showSuccessMessage(addResponse.message);
      }

      handleBack();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
    }
  };

  const transformToCustomerLocationData = (userData: GetUserByIdResponse): CustomerLocationData[] => {
    /* Create a map to aggregate location IDs by customer */
    const customerLocationMap = new Map<number, { customer_name: string; location_ids: number[] }>();

    userData?.userCustomerLocations?.forEach((item) => {
      const { customer_id, customer, customer_location_id } = item;
      if (customer_id != null) {
        if (!customerLocationMap?.has(customer_id)) {
          customerLocationMap?.set(customer_id, {
            customer_name: customer?.customer_name,
            location_ids: []
          });
        }
        customerLocationMap.get(customer_id)?.location_ids.push(customer_location_id);
      }
    });

    /* Convert the map to an array of CustomerLocationData */
    return Array?.from(customerLocationMap?.entries())?.map(([customer_id, { customer_name, location_ids }]) => ({
      customer_id,
      customer_name,
      customer_location_list: location_ids
    }));
  };

  /* EFFECT */

  useEffect(() => {
    const fetchRoles = async () => {
      setRoleListLoading(true);
      try {
        const response = await webUserManagementService.getUserRoles();
        const roles = response["AllRecordsList"];
        setRoleList(roles);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        setRoleListLoading(false);
      }
    };
    fetchRoles();
    const fetchUserById = async (userId: number) => {
      try {
        handleEnableLoading();
        const showDeleted = true;
        const userDetails = await webUserManagementService.getUserById(userId, showDeleted);

        form.setFieldsValue({
          role_id: userDetails?.user?.role?.id,
          username: userDetails?.user?.username,
          first_name: userDetails?.user?.first_name,
          last_name: userDetails?.user?.last_name,
          email_address: userDetails?.user?.email_address,
          mobile_number: userDetails?.user?.mobile_number,
          is_active: userDetails?.user?.is_active
        });

        const formattedData = transformToCustomerLocationData(userDetails?.user);
        setCustomerLocationData(formattedData);
        setRoleName(userDetails?.user?.role?.name);

        /* formatted location List data */
        const formattedLocationList = userDetails?.user?.userCustomerLocations?.map((item) => {
          return {
            ...item,
            id: item?.customerLocation?.id,
            location_name: item?.customerLocation?.location_name
          };
        });
        setFormattedLocationList(formattedLocationList);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableLoading();
      }
    };

    if (id) fetchUserById(+id);
    // eslint-disable-next-line
  }, [id]);

  return { form, id, view, readEnabled, roleName, roleList, formattedLocationList, loading: loading || customerListLoading || roleListLoading || locationListLoading, saving, handleSubmit, handleBack, customerMasterList, locationMasterList, addCustomer, customerLocationData, handleRemoveCustomerLocationData, handleChangeLocation, handleCustomerChange, openCustomerSelection, roleListLoading };
};
